import React, { useEffect, useMemo, useState } from "react";
import { AppBar, Toolbar, IconButton, TextField, InputAdornment, Tooltip, Box, Typography } from "@mui/material";
import logo from "../../assets/images/logo.png";
import { useTranslation } from "react-i18next";
import { AppSettingsActionTypeEnum, useAppSettings } from "../../contexts/app-context/AppContext";
import { useForm } from "react-hook-form";
import MenuIcon from "@mui/icons-material/Menu";
import CloudOff from "@mui/icons-material/CloudOff";
import { useOfflineDetectContext } from "../../contexts/offline-detect-context/UseOfflineDetectContext";
import { UIViewEnum } from "../../common/enums/UIViewEnum";
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import { getGroupRoute } from "../../common/utilities/RouteHelpers";
import { asInputRef } from "../../common/utilities/AsInputRef";
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { isMobile, isTablet } from 'react-device-detect';
import { Search, SettingsOverscan } from "@mui/icons-material";
import MobileSearchBar from "../mobile-search-bar/MobileSearchBar";
import UserMessages from "../user-messages/UserMessages";
import { NavigationSource } from "../../common/enums/NavigationSource";
import useNavigateTo from "../../hooks/use-navigate-to/useNavigateTo";
import { useUserContext } from "../../contexts/user-context/UserContext";

export type AppHeaderV1Props = {
    hideMenu?: boolean,
    hideLogo?: boolean,
    hideSearch?: boolean,
    //hideTools?: boolean,
    onToggleDrawer?: () => void,
};

type SearchFormProp = {
    search: string
};


export default function AppHeaderV1({ onToggleDrawer,
    hideMenu,
    hideLogo,
    hideSearch,
    //hideTools
    /*, tools*/ }: AppHeaderV1Props) {

    const { t } = useTranslation();
    const [appSettings, dispatchAppSettings] = useAppSettings();
    const [userState] = useUserContext();
    const {
        getValues,
        handleSubmit,
        register,
        resetField
    } = useForm();

    const [offlineState] = useOfflineDetectContext();
    const navigateTo = useNavigateTo();
    const routeContext = useRouteContext();

    // State
    const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
    const isPhone = isMobile && !isTablet;

    const theme = useMemo(
        () => {
            return createTheme(appSettings.themeSet[appSettings.themeSet.mode].appHeaderV1);
        },
        [appSettings.themeSet]
    );

    useEffect(() => {

        if (routeContext.navSrc === NavigationSource.SearchResults) {

            const searchValue: string | undefined = getValues("search");

            if ((searchValue?.length ?? 0) > 0) {

                resetField("search");
            }
        }
    },
        [
            getValues,
            resetField,
            routeContext.navSrc
        ]);

    const handleSearch = (data: any) => {

        //alert(`Search for ${JSON.stringify(data)}`);
        const searchData = data as SearchFormProp;

        if (searchData.search) {

            const text = searchData.search.trim();
            if (text.length > 0) {

                const url = `${getGroupRoute(UIViewEnum.SearchResults, routeContext.groupID as number)}&token=${encodeURIComponent(text)}`;
                navigateTo(url);
            }
        }
    };

    const onMaximizeView = () => {
        dispatchAppSettings({
            type: AppSettingsActionTypeEnum.MaximizeContent,
            on: !appSettings.maximizeContent
        });
    };


    return (
        <>
            {/* Standard AppBar */}
            {!mobileSearchOpen &&
                <AppBar position="absolute" sx={{
                    zIndex: isPhone ? theme.zIndex.appBar : theme.zIndex.drawer + 1,
                    transition: theme.transitions.create(["width", "margin"], {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.leavingScreen,
                    })

                }}>
                    <Toolbar variant={appSettings.maximizeContent ? 'dense' : undefined}
                        sx={{
                            paddingLeft: theme.spacing(2),
                            paddingRight: theme.spacing(2)

                        }} >
                        {/* Toggle Drawer Button */}

                        {!hideMenu &&
                            <IconButton
                                edge="start"
                                color="inherit"
                                aria-label="open drawer"
                                onClick={() => { onToggleDrawer && onToggleDrawer(); }}>
                                <Tooltip title={t("appHeader.toggleMenu") as string}>
                                    <MenuIcon />
                                </Tooltip>
                            </IconButton>
                        }


                        {!hideLogo &&
                            <img src={logo} alt="eLynx Technologies" />
                        }
                        <Box sx={{
                            display: "flex",
                            marginLeft: "auto",
                            alignItems: 'center'
                        }}>
                            <Typography
                                variant="body2"
                                color='transparent'
                                sx={{
                                    marginLeft: 1,
                                    marginRight: 1
                                }}>{userState.customer?.id}</Typography>
                            {

                                offlineState.offline && !hideSearch &&
                                <Box sx={{
                                    display: 'flex',
                                    marginTop: 'auto',
                                    marginBottom: 'auto'
                                }}>
                                    <CloudOff
                                        sx={{
                                            marginRight: theme.spacing(1)
                                        }}
                                        fontSize='small' />
                                    {t('appHeader.offline')}
                                </Box>
                            }

                            {!hideSearch && !offlineState.offline &&
                                <>
                                    <Box sx={{ display: appSettings.maximizeContent ? 'none' : { xs: "none", sm: "block" } }}>
                                        {/* Make TextField think it's being used with a dark theme */}
                                        <ThemeProvider theme={theme}>
                                            {/* Desktop Search Input */}
                                            <form onSubmit={handleSubmit(handleSearch)}>
                                                <TextField
                                                    variant="filled"
                                                    label={t("appHeader.search")}
                                                    color="secondary"
                                                    size="small"
                                                    {...asInputRef(register("search"))}
                                                    slotProps={{
                                                        input: {
                                                            endAdornment:
                                                                <InputAdornment position="end">
                                                                    <IconButton
                                                                        color="inherit"
                                                                        type="submit"
                                                                        aria-label="Search entire site">
                                                                        <Search />
                                                                    </IconButton>
                                                                </InputAdornment>
                                                        }
                                                    }} />
                                            </form>
                                        </ThemeProvider>
                                    </Box>

                                    {/* Mobile Search Button */}
                                    <Box sx={{ display: appSettings.maximizeContent ? 'block' : { xs: "block", sm: "none" } }}>
                                        <IconButton color="inherit"
                                            aria-label="Search entire site"
                                            onClick={() => setMobileSearchOpen(true)}>
                                            <Search />
                                        </IconButton>
                                    </Box>
                                </>}

                            {/* Desktop Tools */}
                            {!isPhone && <Box
                                sx={{ display: "block" }}
                            >

                                {!offlineState.offline && <UserMessages />}


                                <IconButton color={appSettings.maximizeContent ? 'secondary' : 'inherit'}
                                    onClick={() => onMaximizeView()}>
                                    <Tooltip title={t("appHeader.tools.maximizeview") as string}>
                                        <SettingsOverscan />
                                    </Tooltip>
                                </IconButton>
                            </Box>}
                        </Box>
                    </Toolbar>
                </AppBar>}
            {/* Mobile Search AppBar */}
            {mobileSearchOpen &&
                <MobileSearchBar onClose={() => setMobileSearchOpen(false)} />
            }
        </>
    );
}
